/* Nationwide CareMatters Florida Insurance Application */
import { sharedQuestions } from "./nationwideAppSharedQuestions"
import { sharedSections } from "./nationwideAppSharedSections"
import { FundingPolicyType } from '@shared';
import { nationwideSharedDefs } from './nationwideSharedDefs';
import { nationwidePreApplicationFormDefinition } from '@shared/policy-application/nationwidePreApplicationFormDef';


const questions: Record<string, PolicyFormQuestion> = {
  ...sharedQuestions,

  /* Section 15 - Agent */
  agentHealthPolicies : {
    id: 'agent-health-policies',
    questionRef: "agentHealthPolicies",
    title: 'List all other Life or Health Insurance, Annuities and/or Long-Term Care Insurance you have sold to the applicant.',
    type: 'text',
    required: true
  },
  agentActivePolicies : {
    id: 'agent-active-policies',
    questionRef: "agentActivePolicies",
    title: 'List Life or Health Insurance, Annuities and/or Long-Term Care Insurance you sold to the applicant that are still in force.',
    type: 'text',
    required: true
  },
  agentExpiredPolicies : {
    id: 'agent-expired-policies',
    questionRef: "agentExpiredPolicies",
    title: 'List Life or Health Insurance, Annuities and/or Long-Term Care Insurance you sold to the applicant in the past five (5) years \
    that are no longer in force.',
    type: 'text',
    required: true
  },
  agentNameCert: {
    id: 'agent-name-cert',
    questionRef: 'agentNameCert',
    title: 'Name',
    type: 'text',
    required: true,
  },
  firm: {
    id: 'firm',
    questionRef: 'firm',
    title: 'Firm',
    type: 'text',
    required: true,
  },
  license: {
    id: 'license',
    questionRef: 'license',
    title: 'FL License ID #',
    type: 'text',
    required: true,
  },
  agentPhone: {
    id: 'financial-professional-phone',
    questionRef: 'agentPhone',
    title: 'Phone Number',
    type: 'text',
    required: true,
  },
  agentEmailCert: {
    id: 'agent-email-cert',
    questionRef: 'agentEmailCert',
    title: 'E-mail Address',
    type: 'text',
    required: true,
  },
}

export const nationwideCareMattersFLFormDefinition: PolicyFormDefinition = {
  requiredParameters: nationwideSharedDefs.requiredParameters,

  sections: [
    ...nationwidePreApplicationFormDefinition.sections,
      sharedSections.proposedInsured,
      sharedSections.proposedAdditionalInsured,
      sharedSections.policyOwner,
      sharedSections.jointOwner,
      sharedSections.contingentOwner,
      sharedSections.payor,
      sharedSections.beneficiary,
      sharedSections.premiumBilling,
      sharedSections.caremattersIIPlan,
      sharedSections.caremattersTogetherPlan,
      sharedSections.unintendedLapse,
      sharedSections.taxpayerCertification,
      sharedSections.electronicDelivery,
      /* 13 ▸ Insurance Information --------------------------------------------- */
      {
        id: 'insurance-information',
        title: 'Insurance Information',
        subsections: [
          {
            id: 'primary-insured-insurance',
            title: 'Primary Insured Insurance History',
            questionGroups: [
              {
                id: 'primary-current-insurance',
                format: 'default',
                questions: ['primaryInsuredOtherInsurance', 'primaryInsuredSoldPolicy', 'primaryInsuredRecentApplications'],
              },
              {
                id: 'primary-replacement',
                format: 'default',
                questions: ['primaryInsuredReplacement', 'primaryInsuredPastInsurance'],
              },
              {
                id: 'primary-ltc-coverage',
                format: 'default',
                questions: ['primaryInsuredOtherLtc', 'primaryInsuredTerminatedLtc', 'primaryInsuredMedicaid'],
              },
              {
                id: 'primary-intentions',
                format: 'default',
                questions: ['primaryInsuredIntendToStop', 'primaryInsuredReplaceMedical'],
              },
            ],
          },
          {
            id: 'additional-insured-insurance',
            title: 'Additional Insured Insurance History',
            conditional: {
              visible: {
                field: 'fundingPolicyType',
                operator: 'equals' as const,
                value: FundingPolicyType.jointCareMatters,
              },
            },
            questionGroups: [
              {
                id: 'additional-current-insurance',
                format: 'default',
                questions: ['additionalInsuredOtherInsurance', 'additionalInsuredSoldPolicy', 'additionalInsuredRecentApplications'],
              },
              {
                id: 'additional-replacement',
                format: 'default',
                questions: ['additionalInsuredReplacement', 'additionalInsuredPastInsurance'],
              },
              {
                id: 'additional-ltc-coverage',
                format: 'default',
                questions: ['additionalInsuredOtherLtc', 'additionalInsuredTerminatedLtc', 'additionalInsuredMedicaid'],
              },
              {
                id: 'additional-intentions',
                format: 'default',
                questions: ['additionalInsuredIntendToStop', 'additionalInsuredReplaceMedical'],
              },
            ],
          },
            {
            id: 'insurance-coverage-table',
            title: 'Insurance Coverage Details',
            questionGroups: [
              {
                id: 'coverage-table',
                format: 'default',
                questions: ['insuranceCoverageDetails'],
              },
            ],
          },
          {
            id: 'agent-additional',
            title: 'Agent Policy Information',
            questionGroups: [
              {
                id: 'agent',
                format: 'default',
                questions: ['agentHealthPolicies', 'agentActivePolicies', 'agentExpiredPolicies']
              }
            ]
          }
        ],
      },
      sharedSections.financialInformation,
      sharedSections.specialInstructions,
      /* 16 ▸ Agent Certification ------------------------------ */
      {
        id: 'agent',
        title: 'Agent\'s Certification',
        subsections: [
        {
          id: 'professional-info',
          title: 'Professional Information',
          questionGroups: [
            {
              id: 'replacement-certification',
              format: 'default',
              questions: ['insuranceWillReplace'],
            },
            {
              id: 'professional-details',
              format: 'grid',
              questions: ['agentNameCert', 'firm', 'license'],
            },
            {
              id: 'professional-contact',
              format: 'grid',
              questions: ['agentPhone', 'nationwideNumber', 'agentEmailCert'],
            },
          ],
        },
        ],
      },
      sharedSections.formAcknowledgment,
      sharedSections.temporaryInsurance,
  ],

  /* ---------- Question record -------------------------------------------------- */
  questions: {
    ...nationwidePreApplicationFormDefinition.questions,
    ...questions,
  },
};